/**
 * Where a scan flow actually started.
 *
 * WHY THIS EXISTS
 * The funnel envelope's `source` was the literal string 'homepage' on every
 * emitter that did not pass one — 100% of `grass_id_analysis_started` rows, and
 * the same for the ZIP, limit, upgrade and result steps — even when the flow
 * was running on /diagnose, /weed-identifier, a city page or a saved result. So
 * the one property named after entry attribution carried no attribution.
 *
 * THE PROPERTY SPLIT (documented per the Wave 1 brief)
 * A clean merge into `source` alone was NOT possible: `source` is already
 * overloaded across call sites with a call-site kind rather than an origin
 * ('zip_step', 'refinement_photo_slot', 'analysis_loading', 'saved_result',
 * 'analysis_photo_cap', ...). Collapsing those into a route slug would destroy
 * more history than it repaired. So:
 *
 *   - `source` keeps its name and its existing per-call-site values. The only
 *     change is that where it used to be the hardcoded literal 'homepage' it is
 *     now `currentRouteSurface()` — a real value, still comparable with history
 *     because a genuine homepage flow still reports 'homepage'.
 *   - `entry_surface` / `entry_pathname` are new and always mean the same
 *     thing: the surface the SESSION entered on, sticky for the whole session.
 *   - `route_surface` is new and always means the route the event fired on.
 *   - `from_tool` carries the existing ?fromTool attribution, made sticky.
 *
 * Everything here is browser-only by design and returns null under SSR, so
 * `lib/analysis-funnel-envelope.ts` stays import-free and server routes are
 * unaffected.
 */

const ENTRY_PATH_KEY = 'wgit_analysis_entry_path'
const FROM_TOOL_KEY = 'wgit_analysis_from_tool'
const SCAN_SOURCE_KEY = 'wgit_analysis_scan_source'

/** Longest value we will ever stamp on an event, matching the analyze route's
 *  own `scanSource` clamp. */
const MAX_VALUE_LENGTH = 120

function readSession(key: string): string | null {
  if (typeof window === 'undefined') return null
  try {
    return window.sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function writeSession(key: string, value: string): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(key, value)
  } catch {
    /* private mode / storage disabled — the derived value just stops being sticky */
  }
}

function slugify(segment: string): string {
  return segment
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, MAX_VALUE_LENGTH)
}

/**
 * Stable slug for a route. Named surfaces stay hand-mapped so the funnel keeps
 * reading in English; everything else degrades to a slug of the first two path
 * segments rather than to 'unknown'.
 */
export function surfaceFromPathname(pathname: string | null | undefined): string {
  if (!pathname) return 'unknown'
  const path = pathname.split('?')[0].split('#')[0]
  const segments = path.split('/').filter(Boolean)

  if (segments.length === 0) return 'homepage'

  switch (segments[0]) {
    case 'diagnose':
      return 'diagnose'
    case 'weed-identifier':
      return 'weed_identifier'
    case 'what-kind-of-grass-do-i-have':
      return 'grass_id_landing'
    case 'result':
      return 'saved_result'
    case 'dashboard':
      return 'dashboard'
    case 'blog':
      return 'blog'
    case 'lawn-care':
      return 'city_page'
    case 'tools':
      return 'tool_page'
    default:
      return slugify(segments.slice(0, 2).join('_')) || 'unknown'
  }
}

/** Route the event is firing on. Null under SSR. */
export function currentPathname(): string | null {
  if (typeof window === 'undefined') return null
  try {
    return window.location.pathname || '/'
  } catch {
    return null
  }
}

/** Slug for the route the event is firing on. 'unknown' under SSR. */
export function currentRouteSurface(): string {
  return surfaceFromPathname(currentPathname())
}

/**
 * Remember the pathname this session started on. Idempotent: the FIRST call in
 * a session wins, which is the point — later navigations must not rewrite the
 * entry. Safe to call from a provider, a pageview hook, or lazily from the
 * first funnel event of the session (which is what the getters below do).
 */
export function recordSessionEntry(pathname?: string | null): string | null {
  // Snapshot the attribution params on every call, not just the first: they
  // live in a query string that the upload -> ZIP -> analyze navigation drops,
  // so the first call that happens to see them is the only chance to keep them.
  currentFromTool()
  currentScanSource()

  const existing = readSession(ENTRY_PATH_KEY)
  if (existing) return existing
  const next = pathname ?? currentPathname()
  if (!next) return null
  writeSession(ENTRY_PATH_KEY, next.slice(0, MAX_VALUE_LENGTH))
  return next
}

/** Pathname this session entered on. Null under SSR. */
export function sessionEntryPathname(): string | null {
  return recordSessionEntry()
}

/** Slug for the surface this session entered on. Null under SSR. */
export function sessionEntrySurface(): string | null {
  const entry = sessionEntryPathname()
  return entry ? surfaceFromPathname(entry) : null
}

function stickyParam(key: string, storageKey: string): string | null {
  const stored = readSession(storageKey)
  if (stored) return stored
  if (typeof window === 'undefined') return null
  try {
    const value = new URLSearchParams(window.location.search).get(key)
    if (!value) return null
    const trimmed = value.trim().slice(0, MAX_VALUE_LENGTH)
    if (!trimmed) return null
    writeSession(storageKey, trimmed)
    return trimmed
  } catch {
    return null
  }
}

/**
 * The existing `?fromTool=` attribution (lib/city-seo/tool-links.ts,
 * hooks/use-from-tool.ts), made sticky for the session so it survives the
 * upload -> ZIP -> analyze navigation that drops the query string.
 */
export function currentFromTool(): string | null {
  return stickyParam('fromTool', FROM_TOOL_KEY)
}

/**
 * The existing `?scanSource=` concept the analyze route already clamps and
 * stamps on its server events, mirrored client-side so both halves agree.
 */
export function currentScanSource(): string | null {
  return stickyParam('scanSource', SCAN_SOURCE_KEY)
}

/**
 * Value for the funnel envelope's `source`. Callers that have a meaningful
 * call-site kind ('zip_step', 'refinement_result', ...) keep passing it; this
 * is only the replacement for the old hardcoded 'homepage' default.
 */
export function resolveFunnelSource(explicit?: string | null): string {
  if (explicit) return explicit
  return currentScanSource() ?? currentRouteSurface()
}

/** Test seam — clears the sticky session values. */
export function resetEntrySourceForTests(): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(ENTRY_PATH_KEY)
    window.sessionStorage.removeItem(FROM_TOOL_KEY)
    window.sessionStorage.removeItem(SCAN_SOURCE_KEY)
  } catch {
    /* nothing to clear */
  }
}
